/**
 * Pure helpers shared between the React `ThemeProvider` and the blocking
 * `<script>` block in `index.html` that runs before React mounts. The
 * script is duplicated verbatim because a `type="module"` import would
 * defer execution past first paint and reintroduce the dark-mode flash
 * (FOUC). Keep the two in sync; the unit tests here guard the
 * single-source-of-truth.
 *
 * Storage contract:
 *   localStorage['theme:mode'] ∈ {'light', 'dark', 'system'}
 *   localStorage['theme:name'] ∈ registry id (default: 'default')
 */

import type { ResolvedMode, ThemeId, ThemeMode } from './registry';

export const THEME_MODE_KEY = 'theme:mode';
export const THEME_NAME_KEY = 'theme:name';

/**
 * Resolve a stored mode value to one of the three canonical modes.
 * Anything unrecognised (corrupted storage, a value from an older app
 * version) falls back to `system` rather than locking the user into a
 * mode they didn't pick.
 */
export function parseMode(raw: string | null): ThemeMode {
  if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  return 'system';
}

/**
 * Map a `ThemeMode` to whether the `dark` class should be applied right
 * now. `system` consults `prefers-color-scheme`; everything else is a
 * direct mapping.
 */
export function resolveMode(mode: ThemeMode, prefersDark: boolean): ResolvedMode {
  if (mode === 'system') return prefersDark ? 'dark' : 'light';
  return mode;
}

/**
 * Toggle `dark` and `theme-<id>` classes on a host element. Idempotent —
 * removes stale theme-* classes that match the registry shape before
 * applying the new one.
 *
 * Exported so both the FOUC init script (which has its own DOM
 * reference) and the React provider call into the same logic.
 */
export function applyThemeClasses(
  root: { classList: DOMTokenList },
  resolved: ResolvedMode,
  themeId: ThemeId,
): void {
  if (resolved === 'dark') root.classList.add('dark');
  else root.classList.remove('dark');

  // Strip any prior theme-* class so a switch doesn't leave the old one
  // sticking around alongside the new. Capture into an array first because
  // mutating the underlying token list during iteration would skip entries.
  const stale = Array.from(root.classList).filter((c) => c.startsWith('theme-'));
  for (const c of stale) root.classList.remove(c);

  root.classList.add(`theme-${themeId}`);
}
